import { useMemo } from 'react';
import { addDays, addHours, format, parse } from 'date-fns';
import * as yup from 'yup';
import { isDigitalSchedulingOwnerType } from '../../../../../../../types/order/consumer/eCreateSigningAppointmentFormDataOwnerTypes';
import { logicalConditional } from '../../../../../../../utilities/shared/logicalOperators/logicalConditional';
import { useGetCloser } from '../../../../../overview/Summary/summaryTab/emdAmount/hooks/useGetCloser';
import { addressValidation } from './util/addressValidation';
import { validateDate } from './util/dateValidation';
import { isNil } from 'lodash';

export const addressSchemaShape = {
  streetAddress: yup.string().required(),
  unit: yup.string().notRequired().nullable(),
  city: yup.string().required(),
  stateAbbreviation: yup.string().required(),
  zipCode: yup.string().required(),
};

export const nonRequiredAddressSchemaShape = {
  streetAddress: yup.string().notRequired().nullable(),
  unit: yup.string().notRequired().nullable(),
  city: yup.string().notRequired().nullable(),
  stateAbbreviation: yup.string().notRequired().nullable(),
  zipCode: yup.string().notRequired().nullable(),
};

const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
const dateErrorMessage = `Date should be after ${format(new Date(tomorrow), 'MM/dd/yyyy')}.`;

export const dssValidationSchema = {
  dssDate: yup.string().when(['signerSelectsFromRange', 'isDssDateRange'], {
    is: (signerSelectsFromRange?: boolean, isDssDateRange?: boolean) => {
      if (isDssDateRange) {
        return false;
      }

      return !!signerSelectsFromRange;
    },
    then: yup
      .string()
      .required()
      .typeError('Appointment Date is required when Signer selects from range.')
      .test('is-after-or-equal-tomorrow', 'Date must be tomorrow or later.', function (value) {
        const isValid = validateDate(value);
        return (
          isValid ||
          this.createError({
            path: this.path,
            message: dateErrorMessage,
          })
        );
      }),
    otherwise: yup.string().nullable().notRequired(),
  }),
  dssStartTime: yup.string().when(['signerSelectsFromRange'], {
    is: (signerSelectsFromRange?: boolean) => !!signerSelectsFromRange,
    then: yup
      .string()
      .required()
      .typeError('Appointment start time is required when Signer selects from range.')
      .test('is-at-least-1-hour', 'End time must be at least 1 hour after start time.', function (value) {
        const { dssEndTime } = this.parent;
        if (!dssEndTime || !value) return true; // Skip validation if either time is not set

        const startTime = parse(value, 'HH:mm:ss', new Date());
        const endTime = parse(dssEndTime, 'HH:mm:ss', new Date());

        // Calculate the minimum valid end time (start time + 1 hour)
        const minimumEndTime = addHours(startTime, 1);
        const isValid = endTime >= minimumEndTime;
        return (
          isValid ||
          this.createError({
            path: this.path,
            message: `Start time must be at least 1 hour before ${format(endTime, 'h:mm a')}.`,
          })
        );
      }),
    otherwise: yup.string().nullable().notRequired(),
  }),
  dssEndTime: yup.string().when(['signerSelectsFromRange'], {
    is: (signerSelectsFromRange?: boolean) => !!signerSelectsFromRange,
    then: yup
      .string()
      .required()
      .typeError('Appointment end time is required when Signer selects from range.')
      .test('is-at-least-1-hour', 'End time must be at least 1 hour after start time.', function (value) {
        const { dssStartTime } = this.parent;
        if (!dssStartTime || !value) return true; // Skip validation if either time is not set

        const startTime = parse(dssStartTime, 'HH:mm:ss', new Date());
        const endTime = parse(value, 'HH:mm:ss', new Date());

        // Calculate the minimum valid end time (start time + 1 hour)
        const minimumEndTime = addHours(startTime, 1);
        const isValid = endTime >= minimumEndTime;
        return (
          isValid ||
          this.createError({
            path: this.path,
            message: `End time must be at least 1 hour after ${format(startTime, 'h:mm a')}.`,
          })
        );
      }),
    otherwise: yup.string().nullable().notRequired(),
  }),
  dssDateRangeStart: yup.string().when(['isDssDateRange'], {
    is: (isDssDateRange?: boolean) => !!isDssDateRange,
    then: yup
      .string()
      .required()
      .typeError('Appointment start date is required when Signer selects from range.')
      .test('is-after-or-equal-tomorrow', 'Date must be tomorrow or later.', function (value) {
        const isValid = validateDate(value);
        return (
          isValid ||
          this.createError({
            path: this.path,
            message: dateErrorMessage,
          })
        );
      }),
    otherwise: yup.string().nullable().notRequired(),
  }),
  dssDateRangeEnd: yup.string().when(['isDssDateRange'], {
    is: (isDssDateRange?: boolean) => !!isDssDateRange,
    then: yup
      .string()
      .required()
      .typeError('Appointment end date is required when Signer selects from range.')
      .test('is-at-least-1-day-after-start', 'End date must be at least 1 day after start date.', function (value) {
        const { dssDateRangeStart } = this.parent;
        if (!dssDateRangeStart || !value) return true; // Skip validation if either date is not set

        const startDate = parse(dssDateRangeStart, 'yyyy-MM-dd', new Date());
        const endDate = parse(value, 'yyyy-MM-dd', new Date());

        // Calculate minimum valid end date (start date + 1 day)
        const minimumEndDate = addDays(startDate, 1);
        const isValid = endDate >= minimumEndDate;
        return (
          isValid ||
          this.createError({
            path: this.path,
            message: `End date must be at least 1 day after start date.`,
          })
        );
      }),
    otherwise: yup.string().nullable().notRequired(),
  }),
  appointmentDate: yup.string().when(['signerSelectsFromRange'], {
    is: (signerSelectsFromRange?: boolean) => !signerSelectsFromRange,
    then: yup
      .string()
      .required()
      .typeError('Appointment Date is required')
      .test('is-after-or-equal-tomorrow', 'Date must be tomorrow or later.', function (value) {
        const { ownerType, isDigitalScheduling, isSignatureSyncAppointment } = this.parent;
        if (!isDigitalScheduling && isSignatureSyncAppointment) {
          const isValid = validateDate(value, isSignatureSyncAppointment);
          return (
            isValid ||
            this.createError({
              path: this.path,
              message: 'Appointment Date should be today or after',
            })
          );
        }

        if (isDigitalScheduling || isDigitalSchedulingOwnerType(ownerType)) {
          const isValid = validateDate(value);
          return (
            isValid ||
            this.createError({
              path: this.path,
              message: dateErrorMessage,
            })
          );
        }

        return true;
      }),
    otherwise: yup.string().nullable().notRequired(),
  }),
  appointmentTime: yup.string().when(['appointmentDate', 'signerSelectsFromRange'], {
    is: (aptDate?: string | null, signerSelectsFromRange?: boolean) => !signerSelectsFromRange && !isNil(aptDate),
    then: yup.string().required().typeError('Appointment Time is required when an appointment date has been set.'),
    otherwise: yup.string().nullable().notRequired(),
  }),
};

export const commonValidationRules = (hasCloser: boolean = true) => {
  return {
    ...dssValidationSchema,
    ownerType: yup
      .string()
      .test('if-dss-has-closer', 'A digital scheduling appointment requires an assigned closer.', (oType) => {
        return logicalConditional(isDigitalSchedulingOwnerType(oType), hasCloser);
      }),
    notes: yup.string().nullable().max(250, 'Notes must be fewer than 250 characters'),
    address: yup.object().when(['isSignatureSyncAppointment', 'isDigitalScheduling', 'signerWillProvideLocation'], {
      is: (isSigSync?: boolean, isDigSync?: boolean, signerWillProvideLocation?: boolean) =>
        addressValidation(isSigSync, isDigSync, signerWillProvideLocation),
      then: yup.object().shape(addressSchemaShape).required().typeError('Address is required'),
      otherwise: yup.object().shape(nonRequiredAddressSchemaShape).nullable().notRequired(),
    }),
    signingMethod: yup.string().required().typeError('Signing Method is required'),
  };
};

const useDetailsValidationSchema = (isCorrection?: boolean) => {
  const { assignedCloser, isSuccess } = useGetCloser();
  return useMemo(() => {
    const commonRules = commonValidationRules(isSuccess && !!assignedCloser);
    const regularAppointmentValidationSchema = yup.object().shape(
      {
        ...commonRules,
      },
      [['appointmentDate', 'appointmentTime']]
    );

    const correctionAppointmentValidationSchema = yup.object().shape(
      {
        ...commonRules,
        correctionReasons: yup
          .array()
          .of(yup.string())
          .min(1, 'At least one correction reason is required')
          .test(
            'at-least-one-correction-reason',
            'At least one correction reason is required',
            function (correctionReasons) {
              if (!correctionReasons || correctionReasons.length === 0) return true;
              return correctionReasons.some((reason) => !!reason);
            }
          ),
      },
      [['appointmentDate', 'appointmentTime']]
    );

    return !isCorrection ? regularAppointmentValidationSchema : correctionAppointmentValidationSchema;
  }, [isCorrection, assignedCloser, isSuccess]);
};

export default useDetailsValidationSchema;
